import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import api from '../utils/api';
import { 
  ArrowLeft, TrendingUp, TrendingDown, Trophy, Shield, Zap, 
  Target, BarChart3, Layers, Award, AlertTriangle, CheckCircle2,
  ArrowUpRight, ArrowDownRight, Minus
} from 'lucide-react';

const STRATEGY_ICONS = {
  orbis_alpha: <TrendingUp className="w-5 h-5" />,
  orbis_momentum: <Zap className="w-5 h-5" />,
  orbis_defensive: <Shield className="w-5 h-5" />,
  orbis_fibonacci: <Target className="w-5 h-5" />,
  orbis_pattern: <BarChart3 className="w-5 h-5" />,
  orbis_composite: <Layers className="w-5 h-5" />,
};

const PerformanceDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [backtestData, setBacktestData] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    loadPerformanceSummary();
  }, []);

  const loadPerformanceSummary = async () => {
    try {
      const response = await api.get('/performance/summary');
      setSummary(response.data);
    } catch (error) {
      toast.error('Failed to load performance data');
    } finally {
      setLoading(false);
    }
  };

  const loadStrategyDetails = async (strategyId) => {
    setLoadingDetails(true);
    setSelectedStrategy(strategyId);
    
    try {
      const [backtestRes, insightsRes] = await Promise.all([
        api.get(`/performance/backtest/${strategyId}?days=30`),
        api.get(`/performance/insights/${strategyId}`)
      ]);
      setBacktestData(backtestRes.data);
      setInsights(insightsRes.data);
    } catch (error) {
      toast.error('Failed to load strategy details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const getRiskBadgeColor = (risk) => {
    switch (risk) {
      case 'high': return 'bg-red-500/20 text-red-400';
      case 'medium': return 'bg-yellow-500/20 text-yellow-400';
      case 'low': return 'bg-green-500/20 text-green-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };

  const getPnlColor = (pnl) => {
    if (pnl > 0) return 'text-green-400';
    if (pnl < 0) return 'text-red-400';
    return 'text-gray-400';
  };

  const getPnlIcon = (pnl) => {
    if (pnl > 0) return <ArrowUpRight className="w-4 h-4" />;
    if (pnl < 0) return <ArrowDownRight className="w-4 h-4" />;
    return <Minus className="w-4 h-4" />;
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(num);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0D12] text-[#F5F7FA] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#2F6BFF] mx-auto mb-4"></div>
          <p className="text-[#94A3B8]">Loading performance data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0D12] text-[#F5F7FA]">
      {/* Header */}
      <header className="glass-effect border-b border-[#1E293B] sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-8 h-16 flex items-center justify-between">
          <Button
            data-testid="performance-back-btn"
            onClick={() => navigate('/dashboard')}
            variant="ghost"
            className="text-[#94A3B8] hover:text-[#F5F7FA]"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Dashboard
          </Button>
          <span className="text-sm text-[#94A3B8]">30-Day Backtest Results</span>
        </div>
      </header>

      <div className="container mx-auto px-4 md:px-8 py-8">
        {/* Page Title */}
        <motion.div 
          initial={{ y: 20, opacity: 0 }} 
          animate={{ y: 0, opacity: 1 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-semibold font-['Outfit'] mb-2">Strategy Performance</h1>
          <p className="text-[#94A3B8]">Compare strategies based on simulated backtesting results</p>
        </motion.div>

        {/* Recommendation Banner */}
        {summary?.recommendation && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <Card className="bg-gradient-to-r from-[#2F6BFF]/20 to-[#2F6BFF]/5 border-[#2F6BFF]/50">
              <CardContent className="py-4">
                <div className="flex items-center gap-3">
                  <Trophy className="w-6 h-6 text-[#FFD700]" />
                  <div>
                    <p className="text-sm text-[#94A3B8]">Top Recommendation</p>
                    <p className="text-lg font-semibold text-[#F5F7FA]">
                      {STRATEGY_ICONS[summary.recommendation.best_overall]}
                      <span className="ml-2">
                        {summary.strategies.find(s => s.strategy_id === summary.recommendation.best_overall)?.name || 'Best Strategy'}
                      </span>
                    </p>
                  </div>
                  <div className="ml-auto flex gap-6 text-sm">
                    <div>
                      <p className="text-[#94A3B8]">Conservative Pick</p>
                      <p className="text-green-400 flex items-center gap-1">
                        <Shield className="w-4 h-4" />
                        {summary.strategies.find(s => s.strategy_id === summary.recommendation.best_for_conservative)?.name}
                      </p>
                    </div>
                    <div>
                      <p className="text-[#94A3B8]">Aggressive Pick</p>
                      <p className="text-red-400 flex items-center gap-1">
                        <Zap className="w-4 h-4" />
                        {summary.strategies.find(s => s.strategy_id === summary.recommendation.best_for_aggressive)?.name}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Strategy Performance Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {summary?.strategies.map((strategy, idx) => (
            <motion.div
              key={strategy.strategy_id}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 + idx * 0.05 }}
            >
              <Card 
                data-testid={`performance-card-${strategy.strategy_id}`}
                className={`cursor-pointer transition-all h-full ${
                  selectedStrategy === strategy.strategy_id
                    ? 'bg-[#2F6BFF]/10 border-[#2F6BFF]'
                    : 'bg-[#15171C] border-[#1E293B] hover:border-[#2F6BFF]/50'
                }`}
                onClick={() => loadStrategyDetails(strategy.strategy_id)}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-[#2F6BFF]">
                      {STRATEGY_ICONS[strategy.strategy_id]}
                      <CardTitle className="text-lg font-['Outfit']">{strategy.name}</CardTitle>
                    </div>
                    {idx === 0 && (
                      <Award className="w-5 h-5 text-[#FFD700]" title="Top Performer" />
                    )}
                  </div>
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold w-fit ${getRiskBadgeColor(strategy.risk_level)}`}>
                    {strategy.risk_level.charAt(0).toUpperCase() + strategy.risk_level.slice(1)} Risk
                  </span>
                </CardHeader>
                <CardContent>
                  {/* Main P&L */}
                  <div className="mb-4">
                    <p className="text-sm text-[#94A3B8]">30-Day P&L</p>
                    <div className={`text-2xl font-bold flex items-center gap-1 ${getPnlColor(strategy.total_pnl)}`}>
                      {getPnlIcon(strategy.total_pnl)}
                      {formatNumber(strategy.total_pnl)}
                      <span className="text-sm font-normal">({strategy.total_pnl_percentage > 0 ? '+' : ''}{strategy.total_pnl_percentage}%)</span>
                    </div>
                  </div>

                  {/* Key Metrics Grid */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="bg-[#0B0D12] rounded p-2">
                      <p className="text-[#94A3B8]">Win Rate</p>
                      <p className={`font-semibold ${strategy.win_rate >= 55 ? 'text-green-400' : strategy.win_rate >= 50 ? 'text-yellow-400' : 'text-red-400'}`}>
                        {strategy.win_rate}%
                      </p>
                    </div>
                    <div className="bg-[#0B0D12] rounded p-2">
                      <p className="text-[#94A3B8]">Total Trades</p>
                      <p className="font-semibold text-[#F5F7FA]">{strategy.total_trades}</p>
                    </div>
                    <div className="bg-[#0B0D12] rounded p-2">
                      <p className="text-[#94A3B8]">Max Drawdown</p>
                      <p className={`font-semibold ${strategy.max_drawdown <= 10 ? 'text-green-400' : strategy.max_drawdown <= 15 ? 'text-yellow-400' : 'text-red-400'}`}>
                        {strategy.max_drawdown}%
                      </p>
                    </div>
                    <div className="bg-[#0B0D12] rounded p-2">
                      <p className="text-[#94A3B8]">Sharpe Ratio</p>
                      <p className={`font-semibold ${strategy.sharpe_ratio >= 1.5 ? 'text-green-400' : strategy.sharpe_ratio >= 1 ? 'text-yellow-400' : 'text-red-400'}`}>
                        {strategy.sharpe_ratio}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>

        {/* Strategy Details Panel */}
        {selectedStrategy && (
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            className="mb-8"
          >
            <Card className="bg-[#15171C] border-[#1E293B]">
              <CardHeader>
                <div className="flex items-center gap-2">
                  {STRATEGY_ICONS[selectedStrategy]}
                  <CardTitle className="font-['Outfit']">
                    {summary?.strategies.find(s => s.strategy_id === selectedStrategy)?.name} - Detailed Analysis
                  </CardTitle>
                </div>
                <CardDescription className="text-[#94A3B8]">
                  In-depth performance metrics and strategy insights
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingDetails ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#2F6BFF] mx-auto mb-2"></div>
                    <p className="text-[#94A3B8]">Loading details...</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Performance Chart (Simplified) */}
                    {backtestData && (
                      <div>
                        <h3 className="text-lg font-semibold mb-4">Equity Curve (30 Days)</h3>
                        <div className="bg-[#0B0D12] rounded-lg p-4 h-64 relative overflow-hidden">
                          {/* Simple line chart visualization */}
                          <svg viewBox="0 0 400 200" className="w-full h-full">
                            <defs>
                              <linearGradient id="lineGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                                <stop offset="0%" stopColor="#2F6BFF" stopOpacity="0.3" />
                                <stop offset="100%" stopColor="#2F6BFF" stopOpacity="0" />
                              </linearGradient>
                            </defs>
                            
                            {/* Grid lines */}
                            {[0, 1, 2, 3, 4].map(i => (
                              <line 
                                key={i} 
                                x1="0" 
                                y1={i * 50} 
                                x2="400" 
                                y2={i * 50} 
                                stroke="#1E293B" 
                                strokeWidth="1"
                              />
                            ))}
                            
                            {/* Chart line */}
                            {backtestData.daily_data && (() => {
                              const data = backtestData.daily_data;
                              const minBal = Math.min(...data.map(d => d.balance));
                              const maxBal = Math.max(...data.map(d => d.balance));
                              const range = maxBal - minBal || 1;
                              
                              const points = data.map((d, i) => {
                                const x = (i / (data.length - 1)) * 400;
                                const y = 200 - ((d.balance - minBal) / range) * 180 - 10;
                                return `${x},${y}`;
                              }).join(' ');
                              
                              const areaPoints = `0,200 ${points} 400,200`;
                              
                              return (
                                <>
                                  <polygon points={areaPoints} fill="url(#lineGradient)" />
                                  <polyline 
                                    points={points} 
                                    fill="none" 
                                    stroke="#2F6BFF" 
                                    strokeWidth="2"
                                  />
                                </>
                              );
                            })()}
                          </svg>
                          
                          {/* Labels */}
                          <div className="absolute bottom-2 left-2 text-xs text-[#94A3B8]">
                            Start: {formatNumber(backtestData.initial_capital)}
                          </div>
                          <div className="absolute bottom-2 right-2 text-xs text-[#94A3B8]">
                            End: {formatNumber(backtestData.final_balance)}
                          </div>
                        </div>
                        
                        {/* Best/Worst Days */}
                        <div className="grid grid-cols-2 gap-4 mt-4">
                          <div className="bg-green-500/10 border border-green-500/30 rounded p-3">
                            <p className="text-xs text-green-400 mb-1">Best Day</p>
                            <p className="text-lg font-bold text-green-400">
                              +{formatNumber(backtestData.best_day?.pnl || 0)}
                            </p>
                            <p className="text-xs text-[#94A3B8]">{backtestData.best_day?.date}</p>
                          </div>
                          <div className="bg-red-500/10 border border-red-500/30 rounded p-3">
                            <p className="text-xs text-red-400 mb-1">Worst Day</p>
                            <p className="text-lg font-bold text-red-400">
                              {formatNumber(backtestData.worst_day?.pnl || 0)}
                            </p>
                            <p className="text-xs text-[#94A3B8]">{backtestData.worst_day?.date}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Strategy Insights */}
                    {insights && (
                      <div>
                        <h3 className="text-lg font-semibold mb-4">Strategy Insights</h3>
                        
                        {/* Strengths */}
                        <div className="mb-4">
                          <div className="flex items-center gap-2 mb-2">
                            <CheckCircle2 className="w-4 h-4 text-green-400" />
                            <span className="font-medium text-green-400">Strengths</span>
                          </div>
                          <ul className="space-y-1 pl-6">
                            {insights.insights.strengths.map((s, i) => (
                              <li key={i} className="text-sm text-[#94A3B8] list-disc">{s}</li>
                            ))}
                          </ul>
                        </div>

                        {/* Weaknesses */}
                        <div className="mb-4">
                          <div className="flex items-center gap-2 mb-2">
                            <AlertTriangle className="w-4 h-4 text-yellow-400" />
                            <span className="font-medium text-yellow-400">Weaknesses</span>
                          </div>
                          <ul className="space-y-1 pl-6">
                            {insights.insights.weaknesses.map((w, i) => (
                              <li key={i} className="text-sm text-[#94A3B8] list-disc">{w}</li>
                            ))}
                          </ul>
                        </div>

                        {/* Usage Tips */}
                        <div className="bg-[#0B0D12] rounded-lg p-4 space-y-3">
                          <div>
                            <p className="text-xs text-[#94A3B8]">Best Used When</p>
                            <p className="text-sm text-[#F5F7FA]">{insights.insights.best_used_when}</p>
                          </div>
                          <div>
                            <p className="text-xs text-[#94A3B8]">Avoid When</p>
                            <p className="text-sm text-[#F5F7FA]">{insights.insights.avoid_when}</p>
                          </div>
                          <div>
                            <p className="text-xs text-[#94A3B8]">Optimal Timeframe</p>
                            <p className="text-sm text-[#2F6BFF]">{insights.insights.optimal_timeframe}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Disclaimer */}
        <div className="text-center p-4 bg-[#15171C] border border-[#1E293B] rounded-lg">
          <p className="text-xs text-[#94A3B8]">
            Past performance is not indicative of future results. These are simulated backtest results for educational purposes only.
            Trading involves risk and you should not invest money you cannot afford to lose.
          </p>
        </div>
      </div>
    </div>
  );
};

export default PerformanceDashboard;
